import { useEffect, useState } from "react";
import type { ManagePlan } from "@/lib/api";
import { useCreatePlan, useFeatures, useUpdatePlan } from "@/hooks/usePlans";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  EMPTY_PLAN_FORM,
  conflictingPlanFeatureNames,
  missingPlanFeatureNames,
  planToFormValues,
  toggleFeatureId,
  toCreateBody,
  toUpdateBody,
  validatePlanForm,
  type PlanFormErrors,
  type PlanFormValues,
} from "@/components/plan-form";

type PlanFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // null → create mode; a plan → edit mode.
  plan: ManagePlan | null;
};

function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-ink-700">{label}</label>
      {children}
      {error ? (
        <p className="text-[11px] text-[oklch(0.55_0.18_16)]">{error}</p>
      ) : (
        hint && <p className="text-[11px] text-ink-500">{hint}</p>
      )}
    </div>
  );
}

export function PlanFormModal({
  open,
  onOpenChange,
  plan,
}: PlanFormModalProps): React.JSX.Element {
  const isEdit = plan !== null;
  const [values, setValues] = useState<PlanFormValues>(EMPTY_PLAN_FORM);
  const [errors, setErrors] = useState<PlanFormErrors>({});

  const { data: features = [] } = useFeatures();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const isPending = createPlan.isPending || updatePlan.isPending;

  // Reseed the form whenever the modal opens (or switches target plan).
  useEffect(() => {
    if (open) {
      setValues(plan ? planToFormValues(plan) : EMPTY_PLAN_FORM);
      setErrors({});
    }
  }, [open, plan]);

  function toggleFeature(id: string): void {
    setValues((v) => ({ ...v, featureIds: toggleFeatureId(v.featureIds, id, features) }));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const validation = validatePlanForm(values, isEdit);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    if (plan) {
      await updatePlan.mutateAsync({ id: plan.id, ...toUpdateBody(values) });
    } else {
      await createPlan.mutateAsync(toCreateBody(values));
    }
    onOpenChange(false);
  }

  const inputClass = "text-xs";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped at the viewport, with only the fields scrolling: the feature list
          grows with every feature we sell, and a centered dialog taller than the
          screen pushed «Guardar» out of reach. */}
      <DialogContent className="flex max-h-[90dvh] max-w-[440px] flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-ink-900">
            {isEdit ? "Editar plan" : "Nuevo plan"}
          </DialogTitle>
          <DialogDescription className="text-xs text-ink-500">
            {isEdit
              ? "Actualiza el nombre, la posición y las funciones que otorga."
              : "Define la clave, el nombre y las funciones que otorga el plan."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
          <div
            data-testid="plan-form-fields"
            className="-mx-1 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-1"
          >
            <Field
              label="Clave"
              error={errors.key}
              hint={
                isEdit
                  ? "La clave es fija y no puede cambiarse."
                  : "Identificador estable: minúsculas, números y guiones bajos."
              }
            >
              {isEdit ? (
                <div className="w-full rounded-md border border-ink-100 bg-ink-050 px-3 py-2 font-mono text-xs text-ink-500">
                  {values.key}
                </div>
              ) : (
                <Input
                  type="text"
                  placeholder="ej. professional"
                  value={values.key}
                  onChange={(e) => setValues((v) => ({ ...v, key: e.target.value }))}
                  className={inputClass}
                />
              )}
            </Field>

            <Field label="Nombre" error={errors.name}>
              <Input
                type="text"
                placeholder="ej. Profesional"
                value={values.name}
                onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
                className={inputClass}
              />
            </Field>

            <Field
              label="Posición"
              error={errors.position}
              hint="Orden en la lista. Déjalo vacío para asignarlo automáticamente."
            >
              <Input
                type="number"
                min={0}
                placeholder="Automático"
                value={values.position}
                onChange={(e) => setValues((v) => ({ ...v, position: e.target.value }))}
                className={inputClass}
              />
            </Field>

            <Field label="Funciones incluidas">
              {features.length === 0 ? (
                <p className="text-[11px] text-ink-500">
                  No hay funciones disponibles para asociar.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {features.map((f) => {
                    // A feature that only means something on top of another
                    // («Envío a domicilio» needs «Pedidos») stays disabled until
                    // its prerequisite is ticked. Said inline, not in a tooltip:
                    // tooltips never fire on a disabled control or on touch.
                    const checked = values.featureIds.includes(f.id);
                    const missing = checked
                      ? []
                      : missingPlanFeatureNames(f, values.featureIds, features);
                    // Two closes cannot share a plan («Solicitudes de cotización»
                    // and «Pedidos»): the other one stays disabled while one is ticked.
                    const conflicts = checked
                      ? []
                      : conflictingPlanFeatureNames(f, values.featureIds, features);
                    const blocked = missing.length > 0 || conflicts.length > 0;
                    return (
                      <label
                        key={f.id}
                        className={`flex items-center gap-2.5 rounded-md border border-ink-100 px-3 py-2 text-xs text-ink-700 ${
                          blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-ink-050"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={blocked}
                          onChange={() => toggleFeature(f.id)}
                          className="h-4 w-4 accent-moss"
                        />
                        <span className="flex flex-col">
                          <span className="font-medium">{f.name}</span>
                          {missing.length > 0 && (
                            <span className="text-[10px] text-ink-500">
                              Requiere {missing.map((n) => `«${n}»`).join(", ")}
                            </span>
                          )}
                          {conflicts.length > 0 && (
                            <span className="text-[10px] text-ink-500">
                              No se combina con {conflicts.map((n) => `«${n}»`).join(", ")}
                            </span>
                          )}
                        </span>
                        <span className="ml-auto font-mono text-[10px] text-ink-400">{f.key}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </Field>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-ink-100 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear plan"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
